import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { validation, formValues } from './validationSchema';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { exportAnnouncementToXml } from '../../store/actions/announcement';

import { Table, Row, Col, Button, Form, Tooltip, OverlayTrigger } from 'react-bootstrap';

import { fetchWasteCodes } from '../../store/actions/wasteCode';
import { Loader } from '../../utils/tools';
import { loadingCodes } from '../../utils/constants/loadingCodes';
import { acronyms } from '../../utils/constants/acronyms';
import { yCodes } from '../../utils/constants/ycodes';

// Assets
import { ReactComponent as RemoveIcon } from '../../assets/remove.svg';

import UploadXlsx from './upload-xlsx';

const ManualExport = () => {
    const dispatch = useDispatch();

    // Fetch waste codes from database
    useEffect(() => {
        dispatch(fetchWasteCodes());
    }, [dispatch]);

    const currentBranch = useSelector(state => state.branch.current);
    const loading = useSelector(state => state.announcement.loading);
    const wasteCodes = useSelector(state => state.wasteCodes.data);
    const importedData = useSelector(state => state.announcement.import);


    const [rows, setRows] = useState([
        { id: 1, wasteCode: '', wasteName: '', wasteCategory: '', yCode: '', weight: '', loadingCode: '', companyName: '', acronym: '' }
    ]);

    // Formik configuration
    const formik = useFormik({
        initialValues: {
            rows: rows // Initial rows values for Formik
        },
        validationSchema: validation,

        onSubmit: (values) => {
            handleSubmit(values)
        }
    });

    useEffect(() => {
        
        if (importedData && importedData.length > 0) {
            // Pridaj riadky z importedData
            const updatedRows = importedData.map((data, index) => ({
                id: index + 1, // Alebo ak existuje id v importedData, použij ho
                wasteCode: data.wasteCode || '',
                wasteName: data.wasteName || '',
                wasteCategory: data.wasteCategory || '',
                yCode: data.yCode ? 'Y' + data.yCode : '',
                weight: data.wasteWeight || 0,
                loadingCode: data.loadingCode || '',
                companyName: data.identificationNumber + ', ' + data.company || '',
                acronym: data.acronym || ''
            }));
    
            // Nastavenie hodnôt do Formik pomocou setFieldValue
            formik.setFieldValue('rows', updatedRows);
            setRows(updatedRows); // Nastavenie hodnôt pre riadky, ak chceš používať `rows` v komponente
        }
    }, [importedData]);
    
    


    const parseCompanyData = (companyData) => {
        // Regex na spracovanie IČO, názvu firmy, ulice, mesta, PSČ a krajiny (ak je prítomná)
        const regex = /^(\d+),\s*([A-Za-z0-9\s.,\p{L}-]+(?:\s*(?:spol\.?\s*s?\.?\s?r\.o\.?|a\.s\.?|v\.o\.s\.?|k\.s\.?|s\.r\.o\.?)?))\s*,\s*([A-Za-z0-9\s.,\p{L}/-]+)\s*,\s*([A-Za-z\s\p{L}]+)\s*,\s*(\d{3,6}\s?\d{2,6})(?:,\s*(.*))?$/u;
       
        const match = companyData.match(regex);
    
        if (match) {
            const ico = String(match[1]) || 'N/A'; // IČO
            const companyName = match[2]?.trim() || 'N/A'; // Company name with legal form
            const street = match[3]?.trim() || 'N/A'; // Street
            const city = match[4]?.trim() || 'N/A'; // City
            const zip = match[5]?.trim() || 'N/A'; // Zip code
            const country = match[6]?.trim() || 'Slovensko'; // Default to "Slovensko" if country is not provided
            
            return {
                ico, 
                companyName, 
                street, 
                city, 
                zip, 
                country
            };


        } else {
            console.error("Regex did not match input:", companyData); // Log error if regex doesn't match
            return null;
        }
    };    

    // Transform data for export to XML action
    // Input data: identificationNumber, companyName, street, city, zip, country 
    // If country is not received then default country is "Slovensko"
    // Default year: 2024
    // Default activityCode: P
    const transformData = (values) => {
        return values.rows.map(row => {
            const companyData = parseCompanyData(row.companyName); // Get parsed company data
            
            return {
                wasteCode: row.wasteCode,
                wasteName: row.wasteName,
                wasteCategory: row.wasteCategory,
                yCode: row.yCode,
                loadingCode: row.loadingCode,
                client: {
                    address: {
                        street: companyData.street,
                        city: companyData.city,
                        zip: companyData.zip,
                        country: companyData.country
                    },
                    companyName: companyData ? companyData.companyName : row.companyName, // Use parsed company name or original
                    identificationNumber: companyData ? companyData.ico : "N/A" // Use parsed IČO or default "N/A"
                },
                totalIncome: 0,
                totalDisposal: 0, 
                balance: parseFloat(row.weight), 
                acronym: row.acronym || "" // Skratka zo vstupného riadku
            };
        });
    };

    // Handlers
    const handleSubmit = (values) => {
        const year = '2024';
        const activityCode = 'P';
        const announcement = transformData(values);
        dispatch(exportAnnouncementToXml({currentBranch, announcement, year, activityCode}));
    }

    const handleChange = (index, field, value) => {
        const updatedRows = [...rows];
        updatedRows[index][field] = value;

        if (field === 'wasteCode') {
            const foundWaste = wasteCodes.find(item => item.wasteCode === value);
            updatedRows[index].wasteName = foundWaste ? foundWaste.wasteName : '';
            updatedRows[index].wasteCategory = foundWaste ? foundWaste.wasteCategory : '';
        }

        setRows(updatedRows);

        // Update Formik values
        formik.setFieldValue(`rows[${index}].${field}`, value);
    };

    const handleAddRow = () => {
        const newRow = {
            id: rows.length + 1,
            wasteCode: '',
            wasteName: '',
            wasteCategory: '',
            yCode: '',
            weight: '',
            loadingCode: '',
            companyName: '',
            acronym: ''
        };
        setRows([...rows, newRow]);

        // Update Formik values with new row
        formik.setFieldValue('rows', [...rows, newRow]);
    };

    const handleDeleteRow = (index) => {
        const updatedRows = rows.filter((_, i) => i !== index);
        setRows(updatedRows);

        // Update Formik values after deletion
        formik.setFieldValue('rows', updatedRows);
    };

    // Tooltips
    const renderTooltipRemove = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Vymazať riadok
        </Tooltip>
    );

    return (
        <>
            {loading ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '35vh'
                    }}
                >
                    <Loader />
                </div>
            ) : (
                <div>
                    <Form onSubmit={formik.handleSubmit}>
                        <div>
                            <Row>
                                <Col sm="12" md="12">
                                    <UploadXlsx />
                                    {/* Export to XML */}
                                    <Button 
                                        className="button-export btn-custom-small" size="sm"
                                        type="submit" 
                                    >
                                        Exportovať do XML
                                    </Button>
                                </Col>
                            </Row>
                        </div>

                        <div className="content pt-4 pb-4 mb-3">
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th className="px-3">Číslo</th>
                                            <th>Kód odpadu</th>
                                            <th>Názov</th>
                                            <th>Kateg.</th>
                                            <th>Y-kód</th>
                                            <th>Hmotnosť (t)</th>
                                            <th>Kód nakl.</th>
                                            <th>Obchodné meno</th>
                                            <th>Skratka</th>
                                            <th>Akcie</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, index) => (
                                            <tr key={index}>
                                                <td className="px-3">{index + 1}</td>
                                                {/* Waste code */}
                                                <td>
                                                    <Form.Group controlId="formBasicWasteCode">
                                                        <Form.Select
                                                            style={{ width: 'auto', maxWidth: '100%' }}
                                                            placeholder="Vyberte kód odpadu"
                                                            name={`rows[${index}].wasteCode`} // Formik name for dynamic rows
                                                            value={row.wasteCode}
                                                            onChange={(e) => handleChange(index, 'wasteCode', e.target.value)}
                                                        >
                                                            <option value="">Vyberte</option>
                                                            {wasteCodes.map((wasteCode, idx) => (
                                                                <option key={idx} value={wasteCode.wasteCode}>
                                                                    {wasteCode.wasteCode}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </td>
                                                <td>
                                                    <Form.Group>
                                                        <Form.Control
                                                            type="text"
                                                            name={`rows[${index}].wasteName`} // Formik name for dynamic rows
                                                            placeholder="Názov odpadu"
                                                            value={row.wasteName}
                                                            readOnly
                                                        />
                                                    </Form.Group>
                                                </td>
                                                <td>
                                                    <Form.Group>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Kategória odpadu"
                                                            name={`rows[${index}].wasteCategory`} // Formik name for dynamic rows
                                                            value={row.wasteCategory}
                                                            readOnly
                                                        />
                                                    </Form.Group>
                                                </td>
                                                <td>
                                                    <Form.Group>
                                                        <Form.Select
                                                            placeholder="Y kód"
                                                            name={`rows[${index}].yCode`} // Formik name for dynamic rows
                                                            value={row.yCode}
                                                            onChange={(e) => handleChange(index, 'yCode', e.target.value)}
                                                        >
                                                            <option value="">Vyberte</option>
                                                            {yCodes.map((yCode, idx) => (
                                                                <option key={idx} value={yCode.value}>
                                                                    {yCode.value}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </td>
                                                <td>
                                                    <Form.Group>
                                                        <Form.Control
                                                            type="number"
                                                            name={`rows[${index}].weight`} // Formik name for dynamic rows
                                                            value={row.weight}
                                                            onChange={(e) => handleChange(index, 'weight', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </td>
                                                <td>
                                                    <Form.Group>
                                                        <Form.Select
                                                            name={`rows[${index}].loadingCode`} // Formik name for dynamic rows
                                                            value={row.loadingCode}
                                                            onChange={(e) => handleChange(index, 'loadingCode', e.target.value)}
                                                        >
                                                            <option value=""></option>
                                                            {Object.entries(loadingCodes).map(([category, codes]) => (
                                                                <optgroup key={category} label={category}>
                                                                    {codes.map((loadingCode) => (
                                                                        <option key={loadingCode.value} value={loadingCode.value}>
                                                                            {loadingCode.value} - {loadingCode.label}
                                                                        </option>
                                                                    ))}
                                                                </optgroup>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </td>
                                                <td>
                                                    <Form.Group>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Obchodné meno"
                                                            name={`rows[${index}].companyName`} // Formik name for dynamic rows
                                                            value={row.companyName}
                                                            style={{minWidth: '180px'}}
                                                            onChange={(e) => handleChange(index, 'companyName', e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </td>
                                                <td>
                                                    <Form.Select
                                                        name={`rows[${index}].acronym`} // Formik name for dynamic rows
                                                        value={row.acronym}
                                                        onChange={(e) => handleChange(index, 'acronym', e.target.value)}
                                                    >
                                                        <option value="">Vyberte</option>
                                                        {acronyms.map((acronym, idx) => (
                                                            <option key={idx} value={acronym.value}>
                                                                {acronym.label}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </td>
                                                <td>
                                                    <Button
                                                        onClick={() => handleDeleteRow(index)}
                                                        className="btnIcon"
                                                    >
                                                        <OverlayTrigger placement="top" overlay={renderTooltipRemove}>
                                                            <RemoveIcon
                                                                alt="Vymazať"
                                                                className="icon icon-remove"
                                                            />
                                                        </OverlayTrigger>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                        </div>
                    </Form>
                    <Row>
                        <Col>
                            <Button
                                className="btn-custom-small ms-2"
                                size="sm"
                                variant="success"
                                onClick={handleAddRow}
                            >
                                Pridať riadok
                            </Button>
                        </Col>
                    </Row>
                </div>
                
            )}
        </>
    );
};

export default ManualExport;
